import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Adg4Button } from "@/components/adg4/button/src";
import ContentPlatformImage from "@/components/content-platform-image/src";
import { InfoText } from "@/components/info-text/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { RichTextComponent } from "@/templates/agile-hub/src/sections/components/body-text";
import { BeforeYouBegin as BeforeYouBeginType } from "@/templates/agile-hub/tutorial-types";

import { QuestionComponent } from "../../../sections/components/question-component";

export type BeforeYouBeginProps = Omit<BeforeYouBeginType, "name">;

const BeforeYouBeginButton = ({ url, text }: { url: string; text: string }) => {
    const localized = useLocalizedHref(url);
    return (
        <Adg4Button
            variant="primary"
            href={localized}
            css={css({
                marginBlock: "1.5rem 1rem"
            })}
        >
            {text}
        </Adg4Button>
    );
};

const CoreContent = ({
    time,
    audience,
    prerequisite,
    ctaMicrocopy
}: BeforeYouBeginProps) => {
    const intl = useIntl();

    return (
        <>
            {time && (
                <>
                    <p>
                        <b>
                            {intl.formatMessage({
                                id: "agile.hub.before-you-begin.time",
                                defaultMessage: "Time:"
                            })}
                        </b>
                    </p>
                    <p>{time}</p>
                </>
            )}
            {audience && (
                <>
                    <p>
                        <b>
                            {intl.formatMessage({
                                id: "agile.hub.before-you-begin.audience",
                                defaultMessage: "Audience:"
                            })}
                        </b>
                    </p>
                    <RichTextComponent text={audience} name={null} />
                </>
            )}
            {prerequisite && (
                <>
                    <p>
                        <b>
                            {intl.formatMessage({
                                id: "agile.hub.before-you-begin.prerequisite",
                                defaultMessage: "Prerequisite:"
                            })}
                        </b>
                    </p>
                    <RichTextComponent text={prerequisite} name={null} />
                </>
            )}
            {ctaMicrocopy?.ctaUrl && ctaMicrocopy?.ctaButtonText && (
                <BeforeYouBeginButton
                    url={ctaMicrocopy.ctaUrl}
                    text={ctaMicrocopy.ctaButtonText}
                />
            )}
        </>
    );
};

const containerStyles = css({
    display: "grid",
    gap: "2rem",
    marginBlockEnd: "2rem"
});

export const BeforeYouBegin = ({
    relatedAsset,
    relatedQuestions,
    ...props
}: BeforeYouBeginProps) => {
    return (
        <div css={containerStyles}>
            <InfoText hideIcon type="component">
                <CoreContent {...props} />
            </InfoText>
            {relatedAsset && (
                <ContentPlatformImage
                    src={relatedAsset.url}
                    alt={relatedAsset.description as string}
                    contentType={relatedAsset.contentType}
                    baseWidth={728}
                    width={relatedAsset.width}
                    height={relatedAsset.height}
                    style={{
                        maxWidth: "100%"
                    }}
                />
            )}
            {relatedQuestions && <QuestionComponent {...relatedQuestions} />}
        </div>
    );
};
